/* CONTEXT:START
Component: TestJoeDialog
Status: build
Dependencies: Material-UI, mock data service
Next: Task 1.4 - JoeConfigurationHistory component
CONTEXT:END */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Paper,
  LinearProgress
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Psychology as PsychologyIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  AccessTime as AccessTimeIcon,
  Token as TokenIcon
} from '@mui/icons-material';
import { 
  JoeTestRequest,
  JoeTestResponse,
  joeConfigurationApiService
} from '../../services/joeConfigurationApi';

// Props interface
export interface TestJoeDialogProps {
  open: boolean;
  onClose: () => void;
  systemPrompt: string;
  userPromptTemplate: string;
  onApplyConfig?: (systemPrompt: string, userPromptTemplate: string) => Promise<void>;
}

const TestJoeDialog: React.FC<TestJoeDialogProps> = ({
  open,
  onClose,
  systemPrompt,
  userPromptTemplate,
  onApplyConfig
}) => {
  // State management
  const [testContent, setTestContent] = useState<string>('');
  const [testType, setTestType] = useState<'highlight' | 'lowlight'>('highlight');
  const [testResult, setTestResult] = useState<JoeTestResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Sample test content for quick testing
  const sampleContent = {
    highlight: `Our team successfully implemented the new customer onboarding system, resulting in a 25% reduction in processing time and improved customer satisfaction scores. The project was completed on schedule and under budget.`,
    lowlight: `We experienced a significant delay in the Q3 product launch due to unexpected technical challenges with the integration testing phase. This has pushed our timeline back by 6 weeks.`
  };

  // Handle test content change
  const handleTestContentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTestContent(event.target.value);
    setError(null);
  };

  // Handle test type change
  const handleTestTypeChange = (event: any) => {
    const newType = event.target.value as 'highlight' | 'lowlight';
    setTestType(newType);
    setError(null);
    
    // Auto-fill with sample content if current content is empty
    if (!testContent.trim()) {
      setTestContent(sampleContent[newType]);
    }
  };

  // Handle use sample content
  const handleUseSample = () => {
    setTestContent(sampleContent[testType]);
    setError(null);
  };

  // Validate test input
  const validateTestInput = (): string | null => {
    if (!testContent.trim()) {
      return 'Test content is required';
    }
    if (testContent.trim().length < 20) {
      return 'Test content must be at least 20 characters for meaningful evaluation';
    }
    if (testContent.trim().length > 2000) {
      return 'Test content must not exceed 2000 characters';
    }
    return null;
  };

  // Handle run test
  const handleRunTest = async () => {
    const validationError = validateTestInput();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      setTestResult(null);

      const testRequest: JoeTestRequest = {
        systemPrompt,
        userPromptTemplate,
        testContent: testContent.trim(),
        entryType: testType,
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear()
      };

      const result = await joeConfigurationApiService.testConfiguration(testRequest);
      setTestResult(result);

      if (!result.success && result.error) {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to test configuration. Please try again.');
      console.error('Error testing configuration:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle dialog close
  const handleClose = () => {
    if (!isLoading) {
      setTestContent('');
      setTestResult(null);
      setError(null);
      onClose();
    }
  };

  // Handle apply configuration
  const handleApplyConfig = async () => {
    if (onApplyConfig) {
      try {
        setIsLoading(true);
        setError(null);
        await onApplyConfig(systemPrompt, userPromptTemplate);
        // Dialog will be closed by parent component after successful save
      } catch (error) {
        setError('Failed to apply configuration. Please try again.');
        console.error('Error applying configuration:', error);
      } finally {
        setIsLoading(false);
      }
    } else {
      // Fallback: just close dialog if no callback provided
      handleClose();
    }
  };

  // Format processing time
  const formatProcessingTime = (ms: number): string => {
    if (ms < 1000) {
      return `${ms}ms`;
    }
    return `${(ms / 1000).toFixed(1)}s`;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '600px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PsychologyIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" component="div">
            Test Joe Configuration
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Test your custom prompts with sample content to see how Joe will evaluate submissions.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Test Input Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Test Content
          </Typography>
          
          {/* Type Selection */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={testType}
                label="Type"
                onChange={handleTestTypeChange}
                disabled={isLoading}
              >
                <MenuItem value="highlight">Highlight</MenuItem>
                <MenuItem value="lowlight">Lowlight</MenuItem>
              </Select>
            </FormControl>
            
            <Button
              variant="outlined"
              size="small"
              onClick={handleUseSample}
              disabled={isLoading}
            >
              Use Sample Content
            </Button>
          </Box>

          {/* Test Content Input */}
          <TextField
            fullWidth
            multiline
            rows={4}
            value={testContent}
            onChange={handleTestContentChange}
            placeholder={`Enter ${testType} content to test Joe's evaluation...`}
            variant="outlined"
            disabled={isLoading}
            helperText={`${testContent.length}/2000 characters`}
            sx={{ mb: 2 }}
          />

          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Run Test Button */}
          <Button
            variant="contained"
            startIcon={isLoading ? <CircularProgress size={20} /> : <PlayArrowIcon />}
            onClick={handleRunTest}
            disabled={isLoading || !testContent.trim()}
            fullWidth
            sx={{ mb: 2 }}
          >
            {isLoading ? 'Joe is thinking...' : 'Run Test'}
          </Button>

          {/* Loading Progress */}
          {isLoading && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                Testing your configuration with Joe's AI evaluation...
              </Typography>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Test Results Section */}
        <Box sx={{ flexGrow: 1, minHeight: 0 }}>
          <Typography variant="h6" gutterBottom>
            Test Results
          </Typography>

          {!testResult && !isLoading && (
            <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
              <PsychologyIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
              <Typography variant="body1" color="text.secondary">
                Run a test to see how Joe will evaluate your content with the current configuration.
              </Typography>
            </Paper>
          )}

          {testResult && (
            <Box>
              {/* Result Status */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {testResult.success ? (
                  <CheckCircleIcon sx={{ color: 'success.main', mr: 1 }} />
                ) : (
                  <ErrorIcon sx={{ color: 'error.main', mr: 1 }} />
                )}
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {testResult.success ? 'Test Successful' : 'Test Failed'}
                </Typography>
              </Box>

              {/* Processing Metrics */}
              {testResult.success && (
                <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<AccessTimeIcon />}
                    label={`${formatProcessingTime(testResult.processingTimeMs)}`}
                    size="small"
                    variant="outlined"
                  />
                  {testResult.tokenUsage && (
                    <Chip
                      icon={<TokenIcon />}
                      label={`${testResult.tokenUsage} tokens`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Box>
              )}

              {/* Joe's Feedback */}
              {testResult.success && testResult.feedback && (
                <Paper sx={{ 
                  p: 2, 
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  maxHeight: '300px',
                  overflow: 'auto'
                }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main', fontWeight: 600 }}>
                    Joe's Feedback:
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      whiteSpace: 'pre-line',
                      lineHeight: 1.6,
                      '& strong': {
                        fontWeight: 600,
                        color: 'text.primary'
                      }
                    }}
                  >
                    {testResult.feedback}
                  </Typography>
                </Paper>
              )}

              {/* Error Message */}
              {!testResult.success && testResult.error && (
                <Alert severity="error">
                  {testResult.error}
                </Alert>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
        <Button
          onClick={handleClose}
          disabled={isLoading}
          startIcon={<CloseIcon />}
        >
          Close
        </Button>
        
        {testResult?.success && onApplyConfig && (
          <Button
            variant="contained"
            onClick={handleApplyConfig}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
          >
            {isLoading ? 'Applying...' : 'Apply Configuration'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TestJoeDialog;
